import { GameType, MeaningfulDecisionType } from "../Types";

export const MAX_MEANINGFUL_DECISIONS = 1000;

export type DecisionChangeType = Omit<MeaningfulDecisionType, "key" | "month">;

/**
 * Records an accepted simulation change under one lifetime key for that lever or asset. Returning
 * a lever to its original value removes the entry even in a later month, so slider, program,
 * operating, dispatch, and trading churn cannot pad internal balance metrics.
 */
export function recordMeaningfulDecision(
  game: GameType,
  change: DecisionChangeType,
): void {
  if (change.before === change.after) return;
  const month = game.date.monthsElapsed;
  const key = change.lever;
  const log = game.meaningfulDecisions;
  const existingIndex = log.findIndex((entry) => entry.key === key);
  if (existingIndex >= 0) {
    const existing = log[existingIndex];
    if (existing.before === change.after) log.splice(existingIndex, 1);
    else {
      existing.after = change.after;
      existing.label = change.label;
      existing.month = month;
    }
    return;
  }
  if (log.length >= MAX_MEANINGFUL_DECISIONS) return;
  log.push({ ...change, key, month });
}

export function meaningfulDecisionCount(
  game: Pick<GameType, "meaningfulDecisions">,
): number {
  return game.meaningfulDecisions.length;
}

export function meaningfulDecisionCategoryCount(
  decisions: MeaningfulDecisionType[],
): number {
  return new Set(decisions.map(({ kind }) => kind)).size;
}

/** One percent of current peak demand, with a 1 MW floor, is a material grid commitment. */
export function isMaterialCapacityDecision(
  game: GameType,
  capacityW: number,
): boolean {
  const peakDemandW = game.timeline.reduce(
    (peak, tick) => Math.max(peak, tick.demandW || 0),
    0,
  );
  return capacityW >= Math.max(1_000_000, peakDemandW * 0.01);
}

export function validMeaningfulDecisions(
  raw: unknown,
  currentMonth: number,
): raw is MeaningfulDecisionType[] {
  if (!Array.isArray(raw) || raw.length > MAX_MEANINGFUL_DECISIONS)
    return false;
  const keys = new Set<string>();
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) return false;
    const decision = entry as Partial<MeaningfulDecisionType>;
    if (
      typeof decision.key !== "string" ||
      typeof decision.lever !== "string" ||
      !/^[a-z][a-z0-9:-]*$/.test(decision.lever) ||
      typeof decision.label !== "string" ||
      decision.label.trim().length === 0 ||
      decision.label.length > 160 ||
      !Number.isInteger(decision.month) ||
      decision.month! < 0 ||
      decision.month! > currentMonth ||
      decision.key !== decision.lever ||
      typeof decision.kind !== "string" ||
      ![
        "asset",
        "sale",
        "rate",
        "policy",
        "operation",
        "dispatch",
        "trading",
      ].includes(decision.kind) ||
      typeof decision.before !== "string" ||
      typeof decision.after !== "string" ||
      decision.before === decision.after ||
      keys.has(decision.key)
    )
      return false;
    keys.add(decision.key);
  }
  return true;
}
